import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  WEB_DRAWER_PANEL_MAX_WIDTH,
  WEB_MAX_CONTENT_WIDTH,
} from '../../theme/webLayoutWidths';

import { useFocusEffect } from '@react-navigation/native';
import {
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import {
  Button as PaperButton,
  Dialog,
  IconButton,
  Menu,
  Portal,
  SegmentedButtons,
} from 'react-native-paper';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { GTFonts, useLayoutSize, useTheme } from '../../theme';
import { fs, getCurrencyDisplayFormat, iconSize, s, v } from '../../utils/libs';
import {
  composeProductVariantLabel,
  getProductDisplayName,
} from '../../utils/productDisplayName';
import { getApiMessage } from '../../utils/apiError';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import KeyboardAwareFormScroll from '../../components/Reusables/KeyboardAwareFormScroll';
import HeaderIconButton from '../../components/Reusables/headers/HeaderIconButton';
import WebHeaderBar from '../../components/web/WebHeaderBar';
import useWebHeader from '../../components/web/useWebHeader';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import {
  cancelPurchaseOrderInformation,
  getPurchaseOrderInformation,
  getSupplierInvoicesInformation,
  getGoodsReceiptsInformation,
  parsePurchaseOrderResponse,
  parseInvoicesResponse,
  parseGoodsReceiptsResponse,
} from '../../api/backofficeSuppliers';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import {
  PurchaseOrderStatus,
  setGoodsReceiptsByPOIdData,
  setGoodsReceiptsByPOIdError,
  setGoodsReceiptsByPOIdLoading,
  setInvoicesByPOIdData,
  setInvoicesByPOIdError,
  setInvoicesByPOIdLoading,
  setPurchaseOrderByIdData,
  setPurchaseOrderByIdError,
  setPurchaseOrderByIdLoading,
  upsertPurchaseOrderBySupplierId,
} from '../../redux/backoffice/suppliersSlice';
import {
  buildProductFromLine,
  formatQuantityWithUnit,
  parseNumberLike,
} from '../../utils/backofficeSuppliers';
import {
  canAddInvoiceForPO,
  canCreateGoodsReceipt,
  canResendPurchaseOrder,
} from '../../utils/purchaseOrderStatus';
import { formatDisplayDateTime } from '../../utils/formatDisplayDateTime';
import SendPOModal from '../../components/suppliers/SendPOModal';
import POSendResultSection from '../../components/suppliers/POSendResultSection';
import { AppAlert } from '../../services/AppAlert';
import { AppSnackbar } from '../../services/AppSnackbar';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import type { RootStackParamList } from '../../navigation/AppStack';

type RouteParams = {
  supplierId: string;
  purchaseOrderId: string;
};

const getGoodsReceiptLines = (receipt: any): any[] => {
  const source =
    receipt?.bp_goods_receipt_line_items ??
    receipt?.lineItems ??
    receipt?.lines ??
    [];
  return Array.isArray(source) ? source : [];
};

const getGoodsReceiptProductName = (line: any, index: number): string => {
  const name = String(
    line?.bp_gr_line_product_name || line?.productName || '',
  ).trim();
  const variantDisplay = String(
    line?.bp_gr_line_variant_display_name || line?.variantDisplayName || '',
  ).trim();
  if (variantDisplay) {
    return (
      composeProductVariantLabel(name, variantDisplay) || `Item ${index + 1}`
    );
  }
  return name || `Item ${index + 1}`;
};

const getGoodsReceiptQtyLabel = (line: any): string => {
  const qty = parseNumberLike(
    line?.bp_gr_line_quantity_received ?? line?.quantityReceived ?? 0,
  );
  const priceType = String(
    line?.bp_gr_line_price_type || line?.priceType || '',
  ).toLowerCase();
  const isUnit = priceType === 'unit';
  const uom = String(line?.bp_gr_line_unit_of_mass || line?.uom || '').trim();

  return formatQuantityWithUnit(qty, { isUnit, uom });
};

const getLineVariantDisplay = (line: any): string => {
  const explicit = String(
    line?.bp_line_item_variant_display_name || line?.variantDisplayName || '',
  ).trim();
  if (explicit) return explicit;
  const attrs = line?.bp_line_item_variant_attributes || line?.variantAttributes;
  if (attrs && typeof attrs === 'object') {
    const joined = Object.values(attrs)
      .map(value => String(value || '').trim())
      .filter(Boolean)
      .join(' / ');
    if (joined) return joined;
  }
  return '';
};

const BackOfficePurchaseOrderDetailsScreen: React.FC = () => {
  const { theme } = useTheme();
  const { useSidebarNav } = useLayoutSize();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const route = useRoute();
  const { supplierId, purchaseOrderId } = (route.params || {}) as RouteParams;
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const dispatch = useAppDispatch();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();

  const poEntity = useAppSelector(
    state =>
      state.backofficeSuppliers.purchaseOrderById[
      String(purchaseOrderId || '')
      ],
  );
  const invoicesEntity = useAppSelector(
    state =>
      state.backofficeSuppliers.invoicesByPOId[String(purchaseOrderId || '')],
  );
  const receiptsEntity = useAppSelector(
    state =>
      state.backofficeSuppliers.goodsReceiptsByPOId[
      String(purchaseOrderId || '')
      ],
  );

  const purchaseOrder = poEntity?.data || null;
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => getCurrencyDisplayFormat(currency),
    [currency],
  );
  const invoices = Array.isArray(invoicesEntity?.data)
    ? invoicesEntity.data
    : [];
  const goodsReceipts = Array.isArray(receiptsEntity?.data)
    ? receiptsEntity.data
    : [];

  const initialStatus = String(
    purchaseOrder?.bp_purchase_order_status ?? '',
  ).toLowerCase();
  const [activeTab, setActiveTab] = useState<
    'lineItems' | 'invoices' | 'goodsReceipts'
  >(initialStatus !== 'draft' && initialStatus !== '' ? 'goodsReceipts' : 'lineItems');
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendModalMode, setSendModalMode] = useState<'send' | 'resend'>('send');
  const [menuVisible, setMenuVisible] = useState(false);
  const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const allowLeaveRef = useRef(false);

  const getPOStatusColor = useCallback(
    (rawStatus: string) => {
      const status = String(rawStatus || 'draft')
        .trim()
        .toLowerCase();
      if (status === 'sent') return theme.colors.infoDark;
      if (status === 'partially_fulfilled' || status === 'partial')
        return theme.colors.warningDark;
      if (status === 'fulfilled') return theme.colors.successEmerald;
      if (status === 'cancelled' || status === 'canceled')
        return theme.colors.errorDark;
      return theme.colors.textGray700 || theme.colors.textSecondary;
    },
    [theme.colors],
  );

  const fetchPurchaseOrder = useCallback(async () => {
    if (!purchaseOrderId || !owner?.accessToken) return;
    dispatch(setPurchaseOrderByIdLoading(purchaseOrderId));
    try {
      const res = await getPurchaseOrderInformation(
        axiosBackoffice as any,
        purchaseOrderId,
        { accessToken: owner.accessToken },
      );
      const parsed = parsePurchaseOrderResponse(res);
      if (!parsed?.success || !parsed.purchaseOrder) {
        dispatch(
          setPurchaseOrderByIdError({
            id: purchaseOrderId,
            error: parsed?.message || 'Failed to load purchase order',
          }),
        );
        return;
      }
      const po = parsed.purchaseOrder;
      console.log('fetchPurchaseOrder po', po);
      const resolvedPOId = String(po?._id || purchaseOrderId);
      dispatch(
        setPurchaseOrderByIdData({
          id: resolvedPOId,
          data: po,
        }),
      );
      const resolvedSupplierId = String(
        (typeof po.bp_purchase_order_supplier_id === 'object'
          ? po.bp_purchase_order_supplier_id?._id
          : po.bp_purchase_order_supplier_id) || supplierId || '',
      );
      if (resolvedSupplierId) {
        dispatch(
          upsertPurchaseOrderBySupplierId({
            supplierId: resolvedSupplierId,
            order: po,
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        setPurchaseOrderByIdError({
          id: purchaseOrderId,
          error: getApiMessage(error, 'Failed to load purchase order'),
        }),
      );
    }
  }, [axiosBackoffice, dispatch, owner?.accessToken, purchaseOrderId, supplierId]);

  const fetchInvoices = useCallback(async () => {
    if (!purchaseOrderId || !owner?.accessToken) return;
    dispatch(setInvoicesByPOIdLoading(purchaseOrderId));
    try {
      const res = await getSupplierInvoicesInformation(
        axiosBackoffice as any,
        { purchaseOrderId },
        { accessToken: owner.accessToken },
      );
      const parsed = parseInvoicesResponse(res);
      if (!parsed?.success) {
        dispatch(
          setInvoicesByPOIdError({
            id: purchaseOrderId,
            error: parsed?.message || 'Failed to load invoices',
          }),
        );
        return;
      }
      dispatch(
        setInvoicesByPOIdData({
          id: purchaseOrderId,
          data: parsed.invoices || [],
        }),
      );
    } catch (error: any) {
      dispatch(
        setInvoicesByPOIdError({
          id: purchaseOrderId,
          error: getApiMessage(error, 'Failed to load invoices'),
        }),
      );
    }
  }, [axiosBackoffice, dispatch, owner?.accessToken, purchaseOrderId]);

  const fetchGoodsReceipts = useCallback(async () => {
    if (!purchaseOrderId || !owner?.accessToken) return;
    dispatch(setGoodsReceiptsByPOIdLoading(purchaseOrderId));
    try {
      const res = await getGoodsReceiptsInformation(
        axiosBackoffice as any,
        purchaseOrderId,
        { accessToken: owner.accessToken },
      );
      const parsed = parseGoodsReceiptsResponse(res);
      if (!parsed?.success) {
        dispatch(
          setGoodsReceiptsByPOIdError({
            id: purchaseOrderId,
            error: parsed?.message || 'Failed to load goods receipts',
          }),
        );
        return;
      }
      dispatch(
        setGoodsReceiptsByPOIdData({
          id: purchaseOrderId,
          data: parsed.goodsReceipts || [],
        }),
      );
    } catch (error: any) {
      dispatch(
        setGoodsReceiptsByPOIdError({
          id: purchaseOrderId,
          error: getApiMessage(error, 'Failed to load goods receipts'),
        }),
      );
    }
  }, [axiosBackoffice, dispatch, owner?.accessToken, purchaseOrderId]);


  useFocusEffect(
    useCallback(() => {
      if (!owner?.accessToken || !purchaseOrderId) return;
      fetchInvoices();
      fetchPurchaseOrder();
      fetchGoodsReceipts();
    }, [
      fetchGoodsReceipts,
      fetchInvoices,
      fetchPurchaseOrder,
      owner?.accessToken,
      purchaseOrderId,
    ]),
  );

  const lineItems: any[] = useMemo(() => {
    const lines = Array.isArray(purchaseOrder?.bp_purchase_order_line_items)
      ? purchaseOrder?.bp_purchase_order_line_items
      : Array.isArray(purchaseOrder?.lineItems)
        ? purchaseOrder?.lineItems
        : [];
    return Array.isArray(lines) ? lines : [];
  }, [purchaseOrder?.bp_purchase_order_line_items, purchaseOrder?.lineItems]);

  const total = Number(
    purchaseOrder?.bp_purchase_order_total ??
    purchaseOrder?.total ??
    lineItems.reduce((sum, line: any) => {
      const qty = parseNumberLike(
        line?.bp_line_item_quantity ?? line?.bp_line_item_mass,
      );
      const unitPrice = parseNumberLike(line?.bp_line_item_unit_price);
      return sum + qty * unitPrice;
    }, 0),
  );

  const [poStatus, setPoStatus] = useState<PurchaseOrderStatus>(String(purchaseOrder?.bp_purchase_order_status ?? '—'));
  const poStatusColor = getPOStatusColor(poStatus);
  const isDraft = poStatus === 'draft';
  const isPurchaseOrderLoading = !purchaseOrder && Boolean(poEntity?.loading);
  const canAddInvoice = canAddInvoiceForPO(purchaseOrder);
  const canCreateGR = canCreateGoodsReceipt(purchaseOrder);
  const canResend = canResendPurchaseOrder(purchaseOrder);

  const supplier = purchaseOrder?.bp_purchase_order_supplier_id;
  const supplierName =
    typeof supplier === 'object' ? supplier?.bp_supplier_name : '—';
  const createdDateRaw =
    purchaseOrder?.bp_purchase_order_created_at || purchaseOrder?.createdAt;
  const createdDateFormatted = formatDisplayDateTime(createdDateRaw);

  useEffect(() => {
    return navigation.addListener('beforeRemove', e => {
      if (allowLeaveRef.current) return;
      if (!isPurchaseOrderLoading && !isDraft) return;

      e.preventDefault();

      AppAlert.alert(
        isPurchaseOrderLoading
          ? 'Purchase order is still loading'
          : 'Draft purchase order',
        isPurchaseOrderLoading
          ? 'Purchase order data is still loading. Please wait...'
          : "This purchase order will be saved as a draft. You can't create a Goods receipt on a draft purchase order, send it to the supplier first.",
        [
          { text: isPurchaseOrderLoading ? 'Wait' : 'Stay', style: 'cancel' },
          {
            text: 'Leave',
            onPress: () => {
              allowLeaveRef.current = true;

              if (supplierId) {
                navigation.replace('BackOfficeSupplierDetails', {
                  supplierId: String(supplierId),
                });
                return;
              }

              navigation.dispatch(e.data.action);
            },
          },
        ],
        { cancelable: true },
      );
    });
  }, [navigation, isPurchaseOrderLoading, isDraft]);

  useEffect(() => {
    if (purchaseOrder) {
      setPoStatus(String(purchaseOrder?.bp_purchase_order_status));
    }
  }, [purchaseOrder]);

  const handleConfirmCancel = useCallback(async () => {
    if (!purchaseOrderId || !owner?.accessToken || !purchaseOrder) return;
    setCancelling(true);
    try {
      const res = await cancelPurchaseOrderInformation(
        axiosBackoffice as any,
        { purchaseOrderId },
        { accessToken: owner.accessToken },
      );
      const ok = res?.data?.success === true || res?.data?.status === 200;
      if (!ok) {
        AppSnackbar.show(
          String(res?.data?.message || 'Failed to cancel purchase order'),
          'error',
        );
        setCancelling(false);
        return;
      }
      const fromResponse =
        parsePurchaseOrderResponse(res)?.purchaseOrder ??
        res?.data?.purchaseOrder;
      const resolvedPO =
        fromResponse && typeof fromResponse === 'object'
          ? { ...fromResponse, bp_purchase_order_status: 'cancelled' as const }
          : {
            ...purchaseOrder,
            bp_purchase_order_status: 'cancelled' as const,
          };
      dispatch(
        setPurchaseOrderByIdData({
          id: purchaseOrderId,
          data: resolvedPO,
        }),
      );
      if (supplierId) {
        dispatch(
          upsertPurchaseOrderBySupplierId({
            supplierId,
            order: resolvedPO,
          }),
        );
      }
      appAnalytics.track('procurement_po_cancelled', {
        ...buildAppAnalyticsContext(),
      });
      AppSnackbar.show(
        String(res?.data?.message || 'Purchase order cancelled successfully'),
        'success',
      );
      setCancelDialogVisible(false);
    } catch (error: any) {
      AppSnackbar.show(
        getApiMessage(error, 'Failed to cancel purchase order'),
        'error',
      );
    } finally {
      setCancelling(false);
    }
  }, [
    axiosBackoffice,
    dispatch,
    owner?.accessToken,
    purchaseOrder,
    purchaseOrderId,
    supplierId,
  ]);

  const showHeaderMenu = canAddInvoice || canCreateGR || canResend;

  const headerRightNode = useMemo(
    () => (
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Menu
          visible={menuVisible}
          onDismiss={() => setMenuVisible(false)}
          anchor={
            <HeaderIconButton
              tooltip="Open purchase order actions"
              onPress={() => setMenuVisible(true)}
              style={{ padding: s(6) }}
              accessibilityLabel="Open purchase order actions">
              <MaterialCommunityIcons
                name="dots-vertical"
                size={iconSize(20)}
                color={theme.colors.textInverse}
              />
            </HeaderIconButton>
          }
          anchorPosition="bottom"
          contentStyle={{ backgroundColor: theme.colors.surface }}>
          {canAddInvoice && (
            <Menu.Item
              leadingIcon="receipt"
              theme={{ colors: { primary: theme.colors.primaryDark } }}
              onPress={() => {
                setMenuVisible(false);
                navigation.navigate('BackOfficeAddInvoice', {
                  supplierId: String(supplierId || ''),
                  purchaseOrderId: String(purchaseOrderId || ''),
                });
              }}
              title="Add Invoice"
              titleStyle={{ color: theme.colors.primaryDark }}
            />
          )}
          {canCreateGR && (
            <Menu.Item
              leadingIcon="truck-delivery-outline"
              theme={{ colors: { primary: theme.colors.primaryDark } }}
              onPress={() => {
                setMenuVisible(false);
                navigation.navigate('BackOfficeCreateGoodsReceipt', {
                  purchaseOrderId: String(purchaseOrderId || ''),
                  supplierId: String(supplierId || ''),
                });
              }}
              title="Create Goods Receipt"
              titleStyle={{ color: theme.colors.primaryDark }}
            />
          )}
          {canResend && (
            <Menu.Item
              leadingIcon="send"
              theme={{ colors: { primary: theme.colors.primaryDark } }}
              onPress={() => {
                setMenuVisible(false);
                setSendModalMode('resend');
                setShowSendModal(true);
              }}
              title="Resend to Supplier"
              titleStyle={{ color: theme.colors.primaryDark }}
            />
          )}
        </Menu>
      </View>
    ),
    [
      canAddInvoice,
      canCreateGR,
      canResend,
      menuVisible,
      navigation,
      purchaseOrderId,
      supplierId,
      theme.colors.primaryDark,
      theme.colors.surface,
    ],
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: showHeaderMenu ? () => headerRightNode : undefined,
    });
  }, [headerRightNode, navigation, showHeaderMenu]);

  const webHeaderNode = useMemo(() => {
    if (!useSidebarNav) return null;
    return (
      <WebHeaderBar
        title="Purchase Order Details"
        titleAlign="center"
        rightSlot={showHeaderMenu ? headerRightNode : undefined}
        onBack={() => {
          if (navigation.canGoBack()) navigation.goBack();
        }}
      />
    );
  }, [headerRightNode, navigation, showHeaderMenu, useSidebarNav]);

  useWebHeader(webHeaderNode, Boolean(webHeaderNode));

  const renderLineItem = useCallback(
    ({ item, index }: { item: any; index: number }) => {
      const product = buildProductFromLine(item);
      const displayName =
        String(item?.bp_line_item_product_name || '') ||
        getProductLabel(product) ||
        `Line ${index + 1}`;
      const qty = parseNumberLike(
        item?.bp_line_item_quantity ?? item?.bp_line_item_mass,
      );
      const unitPrice = parseNumberLike(item?.bp_line_item_unit_price);
      const isUnit =
        String(item?.bp_line_item_product_price_type || '').toLowerCase() ===
        'unit';
      const uom = String(
        item?.bp_line_item_unit_of_mass ||
        product?.bp_product_unit_of_mass ||
        '',
      );
      const variantDisplay = getLineVariantDisplay(item);
      const variantId = String(item?.bp_line_item_variant_id || '').trim();
      return (
        <View style={styles.listItemCard}>
          <Text style={styles.listItemTitle}>{displayName}</Text>
          {variantDisplay || variantId ? (
            <Text style={styles.listItemMeta}>
              Variant:{' '}
              {variantDisplay || `ID ${variantId.slice(Math.max(0, variantId.length - 6))}`}
            </Text>
          ) : null}
          <Text style={styles.listItemMeta}>
            Quantity: {formatQuantityWithUnit(qty, { isUnit, uom })}
          </Text>
          <Text style={styles.listItemMeta}>
            Unit Price: {currencyText} {unitPrice.toLocaleString()}
            {isUnit && uom.trim() ? `/${uom.trim()}` : ''}
          </Text>
          <Text style={styles.listItemMeta}>
            Line Total: {currencyText} {(qty * unitPrice).toLocaleString()}
          </Text>
        </View>
      );
    },
    [
      currencyText,
      styles.listItemCard,
      styles.listItemMeta,
      styles.listItemTitle,
    ],
  );

  const renderInvoice = useCallback(
    ({ item }: { item: any }) => (
      <View style={styles.listItemCard}>
        <Text style={styles.listItemTitle}>
          {String(
            item?.bp_supplier_invoice_number ||
            `INV ${item?._id?.slice(-6) || ''}`,
          )}
        </Text>
        <Text style={styles.listItemMeta}>
          Amount: {currencyText}{' '}
          {Number(item?.bp_supplier_invoice_total_amount || 0).toLocaleString()}
        </Text>
        {!!item?.bp_supplier_invoice_date && (
          <Text style={styles.listItemMeta}>
            Date: {String(item.bp_supplier_invoice_date).slice(0, 10)}
          </Text>
        )}
      </View>
    ),
    [
      currencyText,
      styles.listItemCard,
      styles.listItemMeta,
      styles.listItemTitle,
    ],
  );

  const renderReceipt = useCallback(
    ({ item }: { item: any }) => {
      const receiptNumber = String(
        item?.bp_goods_receipt_number ||
          `Goods Receipt ${item?._id?.slice(-6) || ''}`,
      );
      const receiptDate = formatDisplayDateTime(
        item?.bp_goods_receipt_date ||
          item?.bp_goods_receipt_created_at ||
          item?.createdAt,
        '—',
        { relativeDayLabels: true },
      );
      const poNumber =
        typeof item?.bp_purchase_order_id === 'object'
          ? String(item?.bp_purchase_order_id?.bp_purchase_order_number || '')
          : '';
      const supplierName =
        typeof item?.bp_supplier_id === 'object'
          ? String(item?.bp_supplier_id?.bp_supplier_name || '')
          : '';
      const lines = getGoodsReceiptLines(item);
      const notes = String(item?.bp_goods_receipt_notes || item?.notes || '').trim();

      return (
        <View style={styles.listItemCard}>
          <View style={styles.receiptHeaderRow}>
            <Text style={styles.listItemTitle}>{receiptNumber}</Text>
            <Text style={styles.receiptDateText}>{receiptDate}</Text>
          </View>

          {!!supplierName && (
            <Text style={styles.listItemMeta}>Supplier: {supplierName}</Text>
          )}
          {!!poNumber && <Text style={styles.listItemMeta}>PO: {poNumber}</Text>}

          <Text style={styles.receiptSummaryText}>
            {lines.length > 0
              ? `${lines.length} item${lines.length === 1 ? '' : 's'} in this receipt`
              : 'Line details not available'}
          </Text>

          {lines.slice(0, 4).map((line: any, index: number) => (
            <View
              key={String(
                line?.id || line?._id || line?.bp_gr_line_po_line_id || index,
              )}
              style={styles.receiptLineRow}>
              <Text style={styles.receiptLineName}>
                {getGoodsReceiptProductName(line, index)}
              </Text>
              <Text style={styles.receiptLineQty}>
                {getGoodsReceiptQtyLabel(line)}
              </Text>
            </View>
          ))}

          {lines.length > 4 && (
            <Text style={styles.listItemMeta}>
              +{lines.length - 4} more item{lines.length - 4 === 1 ? '' : 's'}
            </Text>
          )}

          {!!notes && <Text style={styles.listItemMeta}>Notes: {notes}</Text>}
        </View>
      );
    },
    [styles],
  );

  return (
    <KeyboardAwareFormScroll
      style={styles.container}
      contentContainerStyle={styles.content}
      keyboardShouldPersistTaps="handled"
      // baseBottomPadding={v(20)}
      bottomOffset={v(240)}>
      <View style={styles.tabbedCard}>
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>
            {String(
              purchaseOrder?.bp_purchase_order_number ||
              `Purchase Order ${String(purchaseOrderId || '').slice(-6)}`,
            )}
          </Text>
        </View>
        <View style={styles.infoGrid}>
          <View style={styles.infoRow}>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Status</Text>
              <Text style={[styles.infoValue, { color: poStatusColor }]}>
                {poStatus}
              </Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Supplier</Text>
              <Text style={styles.infoValue}>
                {String(supplierName || '—')}
              </Text>
            </View>
          </View>
          <View style={styles.infoRow}>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Created</Text>
              <Text style={styles.infoValue}>{createdDateFormatted}</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Expected Delivery</Text>
              <Text style={styles.infoValue}>
                {!!purchaseOrder?.bp_purchase_order_expected_delivery_date
                  ? String(
                    purchaseOrder.bp_purchase_order_expected_delivery_date,
                  ).slice(0, 10)
                  : '—'}
              </Text>
            </View>
          </View>
          <View style={styles.infoRow}>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Total</Text>
              <Text style={styles.infoValue}>
                {currencyText} {total.toLocaleString()}
              </Text>
            </View>
            <View style={styles.infoItem}>
              <POSendResultSection
                status={String(purchaseOrder?.bp_purchase_order_status || '')}
                sentVia={purchaseOrder?.bp_purchase_order_sent_via}
                sentAt={purchaseOrder?.bp_purchase_order_sent_at}
                variant="inline"
              />
            </View>
          </View>
        </View>
        {!!purchaseOrder?.bp_purchase_order_notes && (
          <View style={styles.notesRow}>
            <Text style={styles.notesLabel}>Notes: </Text>
            <Text style={styles.notesValue}>
              {String(purchaseOrder.bp_purchase_order_notes)}
            </Text>
          </View>
        )}
        {purchaseOrder && (isDraft) && (
          <View style={styles.draftActionRow}>
            <PaperButton
              buttonColor={theme.colors.primary}
              mode="outlined"
              icon="pencil"
              onPress={() =>
                navigation.navigate('BackOfficePurchaseOrderUpsert', {
                  supplierId: String(supplierId || ''),
                  purchaseOrderId: String(purchaseOrderId || ''),
                })
              }
              style={styles.draftActionBtn}
              contentStyle={styles.draftActionBtnContent}
              labelStyle={styles.draftActionBtnLabel}
              textColor={theme.colors.surface}
              theme={{ colors: { outline: theme.colors.primary } }}
              uppercase={false}>
              Edit
            </PaperButton>
            <PaperButton
              buttonColor={theme.colors.successDark}
              mode="contained"
              icon="send"
              onPress={() => {
                setSendModalMode('send');
                setShowSendModal(true);
              }}
              style={styles.draftActionBtn}
              contentStyle={styles.draftActionBtnContent}
              labelStyle={styles.draftActionBtnLabel}
              uppercase={false}>
              Send to Supplier
            </PaperButton>
            <PaperButton
              mode="outlined"
              icon="close-circle-outline"
              onPress={() => setCancelDialogVisible(true)}
              style={styles.draftActionBtn}
              contentStyle={styles.draftActionBtnContent}
              labelStyle={styles.draftActionBtnLabel}
              textColor={theme.colors.errorDark}
              theme={{ colors: { outline: theme.colors.errorDark } }}
              uppercase={false}>
              Cancel
            </PaperButton>
          </View>
        )}
        <SegmentedButtons
          value={activeTab}
          onValueChange={value => setActiveTab(value as any)}
          buttons={[
            {
              value: 'lineItems',
              label: `Items (${lineItems.length})`,
              style: styles.segmentedButtonItems,
            },
            {
              value: 'goodsReceipts',
              label: `Goods Receipts (${goodsReceipts.length})`,
              style: styles.segmentedButtonGoodsReceipts,
            },
            {
              value: 'invoices',
              label: `Invoices (${invoices.length})`,
              style: styles.segmentedButtonInvoices,
            },

          ]}
          style={styles.segmented}
          theme={{
            colors: {
              secondaryContainer: theme.colors.ActiveSegmentedButton,
              onSecondaryContainer: theme.colors.textGray900,
              outline: theme.colors.border,
            },
          }}
        />
        <View style={styles.tabInfoRow}>
          <MaterialCommunityIcons
            name={
              activeTab === 'lineItems'
                ? 'package-variant'
                : activeTab === 'goodsReceipts'
                  ? 'truck-check-outline'
                  : 'file-document-outline'
            }
            size={iconSize(16)}
            color={theme.colors.textSecondary}
          />
          <Text style={styles.tabInfoText}>
            {activeTab === 'lineItems'
              ? "What you're ordering from this supplier."
              : activeTab === 'goodsReceipts'
                ? "What's actually arrived so far."
                : 'Bills from the supplier for this order.'}
          </Text>
        </View>
        {activeTab === 'lineItems' && (
          <View style={styles.tabContent}>
            <Text style={styles.sectionTitle}>Items</Text>
            <FlatList
              data={lineItems}
              keyExtractor={(item, index) => String(item?._id || index)}
              renderItem={renderLineItem}
              scrollEnabled={false}
              ItemSeparatorComponent={() => (
                <View style={styles.listItemSeparator} />
              )}
              ListEmptyComponent={
                <Text style={styles.emptyText}>No line items found.</Text>
              }
            />
          </View>
        )}
        {activeTab === 'goodsReceipts' && (
          <View style={styles.tabContent}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Goods Receipts</Text>
              {canCreateGR && (
                <PaperButton
                  mode="contained"
                  uppercase={false}
                  icon="truck-delivery-outline"
                  onPress={() =>
                    navigation.navigate('BackOfficeCreateGoodsReceipt', {
                      purchaseOrderId: String(purchaseOrderId || ''),
                      supplierId: String(supplierId || ''),
                    })
                  }
                  buttonColor={theme.colors.successDark}
                  style={styles.tableActionBtn}
                  contentStyle={styles.tableActionBtnContent}
                  labelStyle={styles.tableActionBtnLabel}>
                  Create Goods Receipt
                </PaperButton>
              )}
            </View>
            <FlatList
              data={goodsReceipts}
              keyExtractor={(item, index) => String(item?._id || index)}
              renderItem={renderReceipt}
              scrollEnabled={false}
              ItemSeparatorComponent={() => (
                <View style={styles.listItemSeparator} />
              )}
              ListEmptyComponent={
                <Text style={styles.emptyText}>No goods receipts found.</Text>
              }
            />
          </View>
        )}
        {activeTab === 'invoices' && (
          <View style={styles.tabContent}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Invoices</Text>
              {canAddInvoice && (
                <PaperButton
                  mode="contained"
                  uppercase={false}
                  icon="plus"
                  onPress={() =>
                    navigation.navigate('BackOfficeAddInvoice', {
                      supplierId: String(supplierId || ''),
                      purchaseOrderId: String(purchaseOrderId || ''),
                    })
                  }
                  buttonColor={theme.colors.successDark}
                  style={styles.tableActionBtn}
                  contentStyle={styles.tableActionBtnContent}
                  labelStyle={styles.tableActionBtnLabel}>
                  Add Invoice
                </PaperButton>
              )}
            </View>
            <FlatList
              data={invoices}
              keyExtractor={(item, index) => String(item?._id || index)}
              renderItem={renderInvoice}
              scrollEnabled={false}
              ItemSeparatorComponent={() => (
                <View style={styles.listItemSeparator} />
              )}
              ListEmptyComponent={
                <Text style={styles.emptyText}>No invoices found.</Text>
              }
            />
          </View>
        )}
      </View>

      <Portal>
        <Dialog
          visible={cancelDialogVisible}
          onDismiss={() => !cancelling && setCancelDialogVisible(false)}
          style={[
            styles.cancelDialog,
            { backgroundColor: theme.colors.surface },
          ]}>
          <Dialog.Title style={{ color: theme.colors.text }}>
            Cancel this purchase order?
          </Dialog.Title>
          <Dialog.Content>
            <Text style={{ color: theme.colors.textSecondary }}>
              This cannot be undone.
            </Text>
          </Dialog.Content>
          <Dialog.Actions>
            <PaperButton
              onPress={() => !cancelling && setCancelDialogVisible(false)}
              disabled={cancelling}>
              Don't Cancel
            </PaperButton>
            <PaperButton
              onPress={handleConfirmCancel}
              loading={cancelling}
              disabled={cancelling}
              textColor={theme.colors.errorDark}>
              {cancelling ? 'Cancelling...' : 'Proceed'}
            </PaperButton>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <SendPOModal
        visible={showSendModal}
        mode={sendModalMode}
        purchaseOrderId={String(purchaseOrderId || '')}
        onClose={() => setShowSendModal(false)}
        onSuccess={(res) => {
          const ok = res?.data?.success === true || res?.data?.status === 200;
          if (!ok) return;
          setPoStatus('sent');

          const fromResponse =
            parsePurchaseOrderResponse(res)?.purchaseOrder ??
            res?.data?.purchaseOrder;
          const basePO =
            fromResponse && typeof fromResponse === 'object'
              ? fromResponse
              : purchaseOrder;
          if (!basePO) return;

          const channelsFromResults = Object.entries(
            res?.data?.sendResults ?? {},
          )
            .filter(([, sent]) => sent === true)
            .map(([channel]) => channel);
          const existingVia = Array.isArray(basePO.bp_purchase_order_sent_via)
            ? basePO.bp_purchase_order_sent_via
            : [];
          const sentVia = [
            ...new Set([...existingVia, ...channelsFromResults]),
          ];

          const resolvedPO = {
            ...basePO,
            bp_purchase_order_status: 'sent' as const,
            bp_purchase_order_sent_via: sentVia,
            bp_purchase_order_sent_at:
              basePO.bp_purchase_order_sent_at || new Date().toISOString(),
          };

          dispatch(
            setPurchaseOrderByIdData({
              id: String(purchaseOrderId || ''),
              data: resolvedPO,
            }),
          );
          if (supplierId) {
            dispatch(
              upsertPurchaseOrderBySupplierId({
                supplierId: String(supplierId),
                order: resolvedPO,
              }),
            );
          }
          void fetchPurchaseOrder();
        }}
      />
    </KeyboardAwareFormScroll>
  );
};

const getProductLabel = (product: any) => getProductDisplayName(product);

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
      }),
    },
    cancelDialog: {
      borderRadius: theme.borderRadius.lg,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_DRAWER_PANEL_MAX_WIDTH,
        alignSelf: 'center',
      }),
    },
    content: {
      padding: s(12),
      gap: v(10),
      paddingBottom: v(24),
      ...(Platform.OS === 'web' && {
        padding: s(24),
      }),
    },
    tabbedCard: {
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      padding: s(12),
      paddingTop: s(10),
      ...theme.shadows.sm,
    },
    cardHeader: {
      marginBottom: v(12),
    },
    cardTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(18),
      lineHeight: fs(22),
      color: theme.colors.text,
    },
    infoGrid: {
      marginBottom: v(12),
      gap: v(10),
    },
    infoRow: {
      flexDirection: 'row',
      gap: s(24),
    },
    infoItem: {
      flex: 1,
      gap: v(4),
    },
    infoItemSpacer: {
      flex: 1,
    },
    infoLabel: {
      fontFamily: GTFonts.regular,
      fontSize: fs(11),
      lineHeight: fs(14),
      color: theme.colors.textSecondary,
    },
    infoValue: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    notesRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginBottom: v(8),
    },
    notesLabel: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    notesValue: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.text,
    },
    draftActionRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: s(8),
      marginBottom: v(12),
    },
    draftActionBtn: {
      borderRadius: theme.borderRadius.md,
      elevation: 0,
    },
    draftActionBtnContent: {
      height: v(36),
      justifyContent: 'center',
    },
    draftActionBtnLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
      textAlign: 'center',
    },
    segmented: {
      backgroundColor: theme.colors.surfaceSecondary,
      marginTop: v(16),
      marginBottom: v(16),
    },
    segmentedButtonItems: {
      flex: 0.75,
    },
    segmentedButtonInvoices: {
      flex: 1.1,
    },
    segmentedButtonGoodsReceipts: {
      flex: 2.9,
    },
    tabInfoRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6),
      paddingVertical: v(8),
      paddingHorizontal: s(4),
    },
    tabInfoText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      flex: 1,
    },
    tabContent: {
      gap: v(8),
    },
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: v(12),
    },
    tableActionBtn: {
      borderRadius: theme.borderRadius.md,
      minWidth: s(96),
      elevation: 0,
    },
    tableActionBtnContent: {
      height: v(36),
    },
    tableActionBtnLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
      letterSpacing: 0.2,
    },
    sectionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(19),
      color: theme.colors.text,
    },
    listItemCard: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: theme.borderRadius.md,
      padding: s(10),
      gap: v(4),
    },
    listItemSeparator: {
      height: v(12),
    },
    listItemTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    listItemMeta: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    receiptHeaderRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: s(8),
    },
    receiptDateText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    receiptSummaryText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.text,
      marginTop: v(2),
    },
    receiptLineRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: s(10),
    },
    receiptLineName: {
      flex: 1,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    receiptLineQty: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.text,
    },
    emptyText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
  });

export default BackOfficePurchaseOrderDetailsScreen;
